'use client'

// Client-side UI-event logger — the browser half of the action log.
//
// `logUiEvent(type, data)` buffers events in memory and flushes them in
// batches to POST /api/ui-events (which writes action_log rows with
// method='UI'). Instrumentation call sites are a handful of shared seams:
// coalescedSearchUpdate (panels + filters), onRouterTransitionStart (nav),
// Panel logName (bespoke panels), tabs, the global-search modal, downloadCsv,
// lib/toast (error toasts), and the delegated interaction tracker
// (components/system/interaction-tracker.tsx: clicks, field edits, console
// errors).
//
// Contract: logging can NEVER break or slow the UI. Every entry point is
// try/catch'd to console.debug, calls are enqueue-only (no serialization in
// the caller's frame), and delivery is best-effort — a killed tab loses at
// most a few seconds of events, and an explicit logout drops the last ≤5s
// tail (the sign-out lands before the pagehide flush, so the ingest 204-drops
// the now-unauthenticated batch; known, accepted). Identity is attached
// server-side from the session cookie; nothing here reads user data.
//
// Inert under vitest unless UI_LOG_IN_TESTS is set (mirrors lib/action-log's
// ACTION_LOG_IN_TESTS pattern) so component tests never need fetch mocks.
// Kill switch is server-side: the ingest endpoint drops batches when
// ACTION_LOG_DISABLED is set — no client env var needed.

type UiEventType =
  | 'nav'
  | 'panel.open'
  | 'panel.close'
  | 'filter.change'
  | 'search'
  | 'tab.change'
  | 'export.csv'
  | 'click'
  | 'field.change'
  | 'console.error'
  | 'toast.error'

// Runtime mirror of the UiEventType union, exported for the drift-guard test
// that pins it to the ingest route's EVENT_TYPES allowlist. Server code must
// NOT import from this 'use client' module — the ingest keeps its own copy.
export const UI_EVENT_TYPES = [
  'nav',
  'panel.open',
  'panel.close',
  'filter.change',
  'search',
  'tab.change',
  'export.csv',
  'click',
  'field.change',
  'console.error',
  'toast.error',
] as const

export interface UiEvent {
  type: UiEventType
  data: Record<string, unknown>
  page: string
  ts: number
  seq: number
}

const BUFFER_CAP = 200
const FLUSH_THRESHOLD = 40 // flush early so bursts never hit the cap
const FLUSH_DELAY_MS = 5_000
const PAYLOAD_CAP_CHARS = 50_000 // stay under the ~64KB sendBeacon browser cap
const DWELL_CAP_MS = 4 * 60 * 60 * 1000
const COALESCE_WINDOW_MS = 1_500
const RAGE_REPEATS = 3
const ENDPOINT = '/api/ui-events'

// Query params that ARE panels (?claim=<uuid> etc.) — the URL-param half of
// lib/detail-panels/registry.tsx plus the page-owned panel params that predate
// the registry. Everything else that changes in a same-page URL write is a
// filter. A drift-guard test pins this set to DETAIL_PANEL_PARAMS so new
// registry entries can't silently go unlogged again (8 of them did until
// 2026-08).
const PANEL_PARAMS = new Set([
  'claim',
  'resident',
  'task',
  'payment',
  'batch',
  'account',
  // Check workspace panel (8/13) — the double-width panel over a payment
  // batch; opens are worth the same telemetry as every other drill-in.
  'check',
  // Payer-offset detail panel (detail-view wave, 8/19).
  'offset',
  // Original remit document panel (round 11, 8/19).
  'checkSource',
  'facility',
  'credentialing',
  'openContract',
  'unbilled',
  'teamMember',
  // Command-rail registry drill-ins (7/26). Deliberately NOT 'contract' /
  // 'facility' / 'community' — those are filters or page-owned params.
  'payerContract',
  'facilityRecord',
  'authRecord',
  'credentialingContract',
  'remitBatch',
  'paymentLine',
  // Registry list + resident-aspect drill-ins (missed at registry time;
  // restored 2026-08).
  'claimList',
  'taskList',
  'residentAuths',
  'residentLiability',
  'residentEligibility',
  'residentDiscovery',
  'residentAttendance',
  'residentCoverage',
])

/** Exported for the drift-guard test only. */
export const UI_LOG_PANEL_PARAMS: ReadonlySet<string> = PANEL_PARAMS

let buffer: UiEvent[] = []
// Session-relative ordering: session_id survives reloads (sessionStorage) but
// module state doesn't, so a plain 0-based counter would collide across
// reloads. Seeding from the clock keeps (session_id, seq) effectively unique.
let seq = Date.now() % 1_000_000_000
let flushTimer: ReturnType<typeof setTimeout> | null = null
let lifecycleArmed = false
let initialPageviewLogged = false
const panelOpenedAt = new Map<string, number>()

function isInert(): boolean {
  if (typeof window === 'undefined') return true
  // In the browser bundle process.env.VITEST is undefined; in vitest it's set.
  if (process.env.VITEST && (process.env.UI_LOG_IN_TESTS ?? '').trim() === '') return true
  return false
}

function randomId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}

let fallbackSessionId: string | null = null
function getSessionId(): string {
  try {
    const existing = window.sessionStorage.getItem('ui-log-session')
    if (existing) return existing
    const fresh = randomId()
    window.sessionStorage.setItem('ui-log-session', fresh)
    return fresh
  } catch {
    // Private mode / storage disabled — stable per-module fallback, created
    // lazily so module evaluation can never throw into importing UI code.
    if (!fallbackSessionId) fallbackSessionId = randomId()
    return fallbackSessionId
  }
}

/**
 * The current browsing-session id, or null when inert (SSR / vitest).
 * Consumed by lib/session-fetch-patch.ts to stamp `x-ui-session` on mutation
 * requests so action_log rows join to the UI rows around them.
 */
export function getUiLogSessionId(): string | null {
  try {
    if (isInert()) return null
    return getSessionId()
  } catch {
    return null
  }
}

/**
 * Rotate the session id. Called when a navigation lands on /login so one
 * browser tab can never stitch two people into one session timeline. Flushes
 * first: session_id is attached at flush time, so pending events must ship
 * under the id they belong to.
 */
function rotateSession(): void {
  try {
    flush(false)
    window.sessionStorage.removeItem('ui-log-session')
  } catch {
    // sessionStorage unavailable — rotate the in-memory fallback instead.
  }
  fallbackSessionId = null
}

function byteLength(s: string): number {
  try {
    return new TextEncoder().encode(s).length
  } catch {
    // No TextEncoder (ancient runtime) — chars × 3 over-approximates UTF-8.
    return s.length * 3
  }
}

/** Serialize, trimming oldest events until under the beacon BYTE cap. Returns null if nothing fits. */
function serializeBatch(events: UiEvent[]): string | null {
  while (events.length > 0) {
    const payload = JSON.stringify({ session_id: getSessionId(), events })
    if (byteLength(payload) <= PAYLOAD_CAP_CHARS) return payload
    events = events.slice(1)
  }
  return null
}

function flush(viaBeacon: boolean): void {
  try {
    if (buffer.length === 0) return
    const events = buffer
    buffer = [] // clear first — a racing enqueue starts a fresh batch
    if (flushTimer) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
    const payload = serializeBatch(events)
    if (payload === null) return // even a single event exceeds the cap — drop

    if (viaBeacon && typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
      const ok = navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }))
      if (ok) return
      // fall through to fetch keepalive on beacon refusal
    }
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: payload,
      keepalive: true,
      credentials: 'same-origin',
    }).catch((err) => console.debug('[ui-log] flush failed:', err))
  } catch (err) {
    console.debug('[ui-log] flush failed:', err)
  }
}

function armLifecycleListeners(): void {
  if (lifecycleArmed) return
  lifecycleArmed = true
  window.addEventListener('pagehide', () => flush(true))
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true)
  })
}

// The scrub lives in lib/ui-scrub.ts (a plain module) so the server ingest
// can apply the IDENTICAL scrub without importing from this 'use client'
// file. Re-exported here for the client-side callers (interaction tracker,
// lib/toast).
export { scrubLabel } from '@/lib/ui-scrub'

/**
 * Enqueue one UI event. Enqueue-only in the caller's frame; batched delivery.
 * Never throws.
 *
 * `opts.coalesceKey`: consecutive events with the same type + key inside a
 * 1.5s window collapse into the previous buffered event, incrementing
 * `data.repeat` (and stamping `data.rage: true` at ≥3 — the spam-click
 * signal) instead of pushing a new row.
 *
 * Returns the buffered event (the coalesced-into event when coalescing hit)
 * so delegated callers can annotate it before flush — e.g. the interaction
 * tracker's dead-click verdict lands as `data.dead = true` within its 2s
 * window. Mutations after flush are silently lost; that boundary is
 * accepted. Returns null when inert or dropped.
 */
export function logUiEvent(
  type: UiEventType,
  data: Record<string, unknown>,
  opts?: { coalesceKey?: string },
): UiEvent | null {
  try {
    if (isInert()) return null
    armLifecycleListeners()

    const now = Date.now()
    if (opts?.coalesceKey) {
      const last = buffer[buffer.length - 1]
      if (
        last &&
        last.type === type &&
        typeof last.data._coalesceKey === 'string' &&
        last.data._coalesceKey === opts.coalesceKey &&
        now - last.ts <= COALESCE_WINDOW_MS
      ) {
        const repeat = (typeof last.data.repeat === 'number' ? last.data.repeat : 1) + 1
        last.data.repeat = repeat
        if (repeat >= RAGE_REPEATS) last.data.rage = true
        last.ts = now // keep the window sliding so a steady spam-run stays one row
        return last
      }
    }

    const event: UiEvent = {
      type,
      data: opts?.coalesceKey ? { ...data, _coalesceKey: opts.coalesceKey } : data,
      page: window.location.pathname,
      ts: now,
      seq: seq++,
    }
    buffer.push(event)
    if (buffer.length > BUFFER_CAP) buffer = buffer.slice(buffer.length - BUFFER_CAP)
    if (buffer.length >= FLUSH_THRESHOLD) {
      flush(false)
      return event
    }
    if (!flushTimer) {
      flushTimer = setTimeout(() => {
        flushTimer = null
        flush(false)
      }, FLUSH_DELAY_MS)
    }
    return event
  } catch (err) {
    console.debug('[ui-log] enqueue failed:', err)
    return null
  }
}

/**
 * Classify a same-page URL search-string change into panel.open / panel.close
 * events. Called from coalescedSearchUpdate (the single funnel for panel URL
 * writes). PANELS ONLY: filter changes are logged semantically by FilterChip
 * (components/ui/filter-chip.tsx) — logging non-panel params here too would
 * double-count the URL-driven filter surfaces. O(changed params); never throws.
 */
export function logSearchParamChange(beforeSearch: string, afterSearch: string): void {
  try {
    if (isInert()) return
    const before = new URLSearchParams(beforeSearch)
    const after = new URLSearchParams(afterSearch)
    const keys = new Set([...before.keys(), ...after.keys()])
    for (const key of keys) {
      if (!PANEL_PARAMS.has(key)) continue
      // getAll: diff on ALL values so repeated params can't hide a change.
      const prevAll = before.getAll(key)
      const nextAll = after.getAll(key)
      const prev = prevAll.length > 0 ? prevAll.join(',') : null
      const next = nextAll.length > 0 ? nextAll.join(',') : null
      if (prev === next) continue
      if (prev !== null) {
        const openedAt = panelOpenedAt.get(`${key}:${prev}`)
        panelOpenedAt.delete(`${key}:${prev}`)
        const dwell = openedAt ? Math.min(Date.now() - openedAt, DWELL_CAP_MS) : null
        logUiEvent('panel.close', { param: key, id: prev, dwell_ms: dwell })
      }
      if (next !== null) {
        // Entries whose close happens via full navigation are never deleted;
        // cap the map so a marathon session can't grow it unboundedly.
        if (panelOpenedAt.size > 200) panelOpenedAt.clear()
        panelOpenedAt.set(`${key}:${next}`, Date.now())
        logUiEvent('panel.open', { param: key, id: next })
      }
    }
  } catch (err) {
    console.debug('[ui-log] param-diff failed:', err)
  }
}

/**
 * Log a client navigation. Called from the onRouterTransitionStart wrapper in
 * instrumentation-client.ts. Same-path transitions are skipped — those are
 * panel/filter writes already captured semantically by logSearchParamChange.
 * Records the ATTEMPTED navigation (an aborted transition still logs).
 * Landing on /login rotates the session id (see rotateSession).
 */
export function logNavTransition(href: string, navigationType: string): void {
  try {
    if (isInert()) return
    const to = new URL(href, window.location.origin)
    if (to.pathname === window.location.pathname) return
    logUiEvent('nav', {
      from: window.location.pathname + window.location.search,
      to: to.pathname + to.search,
      kind: navigationType || 'push',
    })
    if (to.pathname === '/login') rotateSession()
  } catch (err) {
    console.debug('[ui-log] nav failed:', err)
  }
}

/** Log the initial pageview once per page load (module-init from instrumentation-client). */
export function logInitialPageview(): void {
  try {
    if (isInert() || initialPageviewLogged) return
    initialPageviewLogged = true
    // A full load landing on /login (logout redirect, session expiry) starts
    // a fresh session BEFORE the pageview so the new id owns it.
    if (window.location.pathname === '/login') rotateSession()
    logUiEvent('nav', {
      from: null,
      to: window.location.pathname + window.location.search,
      kind: 'load',
    })
  } catch (err) {
    console.debug('[ui-log] pageview failed:', err)
  }
}
